import { Fragment, useState } from 'react';
import { Card } from '../../../components/Card';
import { IAvailableQuiz } from '../../../interfaces/IPlayerPerformance';

type FilteredQuiz = IAvailableQuiz & {
  accuracyPercentage: number;
  pointsEarned: number;
  awarded: boolean;
  nOfAttempts?: number;
};

interface QuizListProps {
  filteredQuizzes: FilteredQuiz[];
  searchTerm: string;
  selectedStatus: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
}

export function QuizList({
  filteredQuizzes,
  searchTerm,
  selectedStatus,
  onSearchChange,
  onStatusChange,
}: QuizListProps) {
  const [expandedQuizId, setExpandedQuizId] = useState<string | null>(null);

  return (
    <div className="mb-3">
      <Card shadow>
        {/* Título e barra de pesquisa */}
        <h6 className="fw-semibold mb-3">Pesquise pelo nome do quiz</h6>
        <div className="row mb-4">
          <div className="input-group">
            <span className="input-group-text bg-transparent border-end-0">
              <i
                className="material-icons-outlined"
                style={{ fontSize: '18px' }}
              >
                search
              </i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Nome do quiz"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* Filtros de status */}
        <div className="mb-4">
          <div className="mb-2 fw-semibold small text-end">
            Status do engajamento
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end">
            <button
              type="button"
              className={`btn btn-sm d-flex align-items-center gap-1 ${selectedStatus === 'not-answered' ? 'btn-light border-dark' : 'btn-outline-secondary'}`}
              onClick={() =>
                onStatusChange(
                  selectedStatus === 'not-answered' ? 'all' : 'not-answered'
                )
              }
            >
              <i
                className="material-icons-outlined"
                style={{ fontSize: '16px', color: '#dc3545' }}
              >
                close
              </i>
              <span style={{ color: '#000' }}>Não respondido</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm d-flex align-items-center gap-1 ${selectedStatus === 'answered' ? 'btn-light border-dark' : 'btn-outline-secondary'}`}
              onClick={() =>
                onStatusChange(
                  selectedStatus === 'answered' ? 'all' : 'answered'
                )
              }
            >
              <i
                className="material-icons-outlined"
                style={{ fontSize: '16px', color: '#198754' }}
              >
                check_circle
              </i>
              <span style={{ color: '#000' }}>Respondido</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm d-flex align-items-center gap-1 ${selectedStatus === 'not-awarded' ? 'btn-light border-dark' : 'btn-outline-secondary'}`}
              onClick={() =>
                onStatusChange(
                  selectedStatus === 'not-awarded' ? 'all' : 'not-awarded'
                )
              }
            >
              <i
                className="material-icons-outlined"
                style={{ fontSize: '16px', color: '#6c757d' }}
              >
                emoji_events
              </i>
              <span style={{ color: '#000' }}>Não premiado</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm d-flex align-items-center gap-1 ${selectedStatus === 'awarded' ? 'btn-light border-dark' : 'btn-outline-secondary'}`}
              onClick={() =>
                onStatusChange(selectedStatus === 'awarded' ? 'all' : 'awarded')
              }
            >
              <i
                className="material-icons-outlined"
                style={{ fontSize: '16px', color: '#ffc107' }}
              >
                emoji_events
              </i>
              <span style={{ color: '#000' }}>Premiado</span>
            </button>
          </div>
        </div>

        {/* Lista de quizzes */}
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="border-0 ps-3">Nome do Quiz</th>
                <th className="border-0 text-end pe-3">Status</th>
                <th className="border-0 text-end pe-3">Acerto</th>
                <th
                  className="border-0 text-center pe-3"
                  style={{ width: '40px' }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {filteredQuizzes.length > 0 ? (
                filteredQuizzes.map((quiz) => (
                  <Fragment key={quiz.actionId}>
                    <tr
                      className="border-top"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setExpandedQuizId(
                          expandedQuizId === quiz.actionId
                            ? null
                            : quiz.actionId
                        )
                      }
                    >
                      <td className="ps-3">
                        <span className="fw-medium">{quiz.actionTitle}</span>
                      </td>
                      <td className="text-end pe-3">
                        <div className="d-flex justify-content-end gap-2 align-items-center">
                          {!quiz.hasAnswered ? (
                            <i
                              className="material-icons-outlined"
                              style={{ fontSize: '20px', color: '#dc3545' }}
                              title="Não respondido"
                            >
                              close
                            </i>
                          ) : (
                            <>
                              <i
                                className="material-icons-outlined"
                                style={{ fontSize: '20px', color: '#198754' }}
                                title="Respondido"
                              >
                                check_circle
                              </i>
                              {quiz.awarded ? (
                                <i
                                  className="material-icons-outlined"
                                  style={{ fontSize: '20px', color: '#ffc107' }}
                                  title="Premiado"
                                >
                                  emoji_events
                                </i>
                              ) : (
                                <i
                                  className="material-icons-outlined"
                                  style={{ fontSize: '20px', color: '#6c757d' }}
                                  title="Não premiado"
                                >
                                  emoji_events
                                </i>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="text-end pe-3">
                        {quiz.hasAnswered && (
                          <span style={{ color: '#000' }}>
                            {Math.round(quiz.accuracyPercentage)}%
                          </span>
                        )}
                      </td>
                      <td className="text-center pe-3">
                        <i
                          className="material-icons-outlined"
                          style={{
                            fontSize: '20px',
                            transition: 'transform 0.2s',
                            transform:
                              expandedQuizId === quiz.actionId
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                          }}
                        >
                          expand_more
                        </i>
                      </td>
                    </tr>
                    {expandedQuizId === quiz.actionId && (
                      <tr>
                        <td colSpan={4} className="bg-light px-4 py-3">
                          <div className="row g-3">
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Pontuação válida
                              </div>
                              <div className="fw-semibold">
                                {quiz.maxPoints ?? '--'} pontos
                              </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Mínimo de acertos
                              </div>
                              <div className="fw-semibold">
                                {quiz.minimumScore != null
                                  ? `${quiz.minimumScore}%`
                                  : '--'}
                              </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Total de tentativas realizadas
                              </div>
                              <div className="fw-semibold">
                                {quiz.nOfAttempts ?? '--'}
                              </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Número de questões
                              </div>
                              <div className="fw-semibold">
                                {quiz.numberOfQuestions ?? '--'}
                              </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Tentativas possíveis
                              </div>
                              <div className="fw-semibold">
                                {quiz.amountOfRetries ?? '--'}
                              </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                              <div className="small text-muted">
                                Data de encerramento
                              </div>
                              <div className="fw-semibold">
                                {quiz.endDate
                                  ? new Date(quiz.endDate).toLocaleDateString(
                                      'pt-BR'
                                    )
                                  : '--'}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-muted">
                    Nenhum quiz encontrado com os filtros atuais
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <style>{`
        .table > :not(:first-child) {
          border-top: 0;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }

        .badge {
          font-weight: 500;
          padding: 0.35em 0.65em;
          font-size: 0.75em;
        }

        .input-group-text {
          background-color: transparent;
        }

        .input-group .form-control:focus {
          box-shadow: none;
          border-color: #86b7fe;
        }
      `}</style>
    </div>
  );
}
